/**
 * Tenant Site — published-only model from public Site API (S1 · S8 ↔ S11)
 * UX 안전: fetch 타임아웃 (synaxion-core/04-safety-standards/UX_SAFETY_RULES.md)
 */

import type { SiteCollectedRecord, TenantSiteModel } from '@shared/site/types/tenant-site-model';
import type { SiteAudienceManifest } from '@shared/site/types/site-audience-manifest';
import type { SiteSlugHistoryEntry } from '@shared/site/types/site-slug-history';
import type { SiteSurfaceProjectionV0 } from '@shared/site/publication';
import { EMPTY_AUDIENCE_MANIFEST } from '@shared/site/types/site-audience-manifest';
import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import { API_REQUEST_TIMEOUT_MS } from '@frontend/config/api';
import { fetchWithAuth } from '@frontend/services/authFetch';
import { getAuthHeaders } from '@frontend/utils/jwtUtils';

const baseUrl = (): string => resolveApiOrigin();

function fetchWithTimeout(url: string, init?: RequestInit): Promise<Response> {
  return fetchWithAuth(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...((init?.headers as Record<string, string> | undefined) ?? {}),
    },
    signal: init?.signal ?? AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
}

export interface TenantSiteModelPayload {
  model: TenantSiteModel;
  records: SiteCollectedRecord[];
  audienceManifest: SiteAudienceManifest;
  slugHistory: SiteSlugHistoryEntry[];
  objectPublicationProjections: SiteSurfaceProjectionV0[];
}

interface PublishedSiteModelResponse {
  success: boolean;
  data: {
    publishedModel: TenantSiteModel;
    records?: SiteCollectedRecord[];
    audienceManifest?: SiteAudienceManifest;
    slugHistory?: SiteSlugHistoryEntry[];
    objectPublicationProjections?: SiteSurfaceProjectionV0[];
    publishState: 'published';
  };
}

/** Guest / site-plane resolver — published snapshot only (no draft/preview fallback). */
export async function fetchTenantSitePublishedModel(
  tenantId: string
): Promise<TenantSiteModelPayload | null> {
  const res = await fetchWithTimeout(`${baseUrl()}/api/site/model`, {
    headers: {
      'x-tenant-id': tenantId,
    },
  });

  const body = (await res.json()) as PublishedSiteModelResponse & {
    code?: string;
    error?: string;
  };

  if (!res.ok) {
    if (body.code === 'NOT_PUBLISHED' || res.status === 404) {
      return null;
    }
    throw new Error(body.error ?? `Published site model HTTP ${res.status}`);
  }

  const model = body.data?.publishedModel;
  if (!model || body.data.publishState !== 'published') {
    return null;
  }

  return {
    model,
    records: body.data.records ?? [],
    audienceManifest: body.data.audienceManifest ?? EMPTY_AUDIENCE_MANIFEST,
    slugHistory: body.data.slugHistory ?? [],
    objectPublicationProjections: body.data.objectPublicationProjections ?? [],
  };
}

/** @deprecated Use fetchTenantSitePublishedModel — kept for test mocks */
export const fetchTenantSiteModelFromStudio = fetchTenantSitePublishedModel;
